import { useEffect, useMemo, useRef, useState } from "react";
import { readPalette } from "../../lib/three/palette.js";
import "./mood-field.css";

/**
 * Hosts the mood field.
 *
 * Everything expensive is conditional: three is behind a dynamic import, the
 * render loop only runs while the card is actually on screen and the tab is
 * visible, and prefers-reduced-motion gets a single composed frame instead of
 * an animation.
 *
 * If anything at all goes wrong — no WebGL, a lost context, a failed chunk —
 * the component quietly reports `failed` and the caller keeps its CSS wash.
 * There is no state in which this renders an empty rectangle.
 */

/** Cheap capability probe; the context is thrown away immediately. */
function hasWebGL() {
  if (typeof document === "undefined") return false;
  try {
    const probe = document.createElement("canvas");
    const gl = probe.getContext("webgl2") || probe.getContext("webgl");
    if (!gl) return false;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

export default function MoodFieldCanvas({ pairs, onStatusChange }) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const statusRef = useRef(null);
  const [ready, setReady] = useState(false);

  // computeMoodEvidence hands back a fresh array every render. Collapse it to a
  // string first so the scene isn't torn down and rebuilt on every parent
  // re-render — the entrance would restart each time.
  const signature = (pairs || []).map((p) => `${p.before}>${p.after}`).join(",");
  const rises = useMemo(
    () =>
      signature
        ? signature.split(",").map((seg) => {
            const [before, after] = seg.split(">");
            return { before: Number(before), after: Number(after) };
          })
        : [],
    [signature]
  );

  // Kept in a ref so a new callback identity from the parent can never tear
  // down and rebuild the scene.
  useEffect(() => {
    statusRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas || rises.length === 0) return;

    let field = null;
    let cancelled = false;
    let observer = null;
    let resizeObserver = null;
    let onVisibility = null;
    let onThemeChange = null;
    let onMotionChange = null;
    let onContextLost = null;
    let inView = false;
    const report = (status) => statusRef.current?.(status);

    if (!hasWebGL()) {
      report("failed");
      return;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const themeQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const sync = () => {
      if (!field) return;
      const shouldRun = inView && !document.hidden && !motionQuery.matches;
      if (shouldRun) field.play();
      else field.pause();
    };

    (async () => {
      try {
        const { createMoodField } = await import("../../lib/three/mood-field.js");
        if (cancelled) return;

        field = await createMoodField({
          canvas,
          pairs: rises,
          palette: readPalette(host),
          reducedMotion: motionQuery.matches,
        });
        if (cancelled) {
          field.dispose();
          return;
        }

        const measure = () => {
          const rect = host.getBoundingClientRect();
          field?.resize(rect.width, rect.height, window.devicePixelRatio || 1);
        };
        measure();
        setReady(true);
        report("ready");

        onContextLost = (event) => {
          // A lost context is unrecoverable here; hand the surface back to CSS.
          event.preventDefault();
          field?.pause();
          setReady(false);
          report("failed");
        };
        canvas.addEventListener("webglcontextlost", onContextLost);

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(measure);
          resizeObserver.observe(host);
        }

        if (typeof IntersectionObserver !== "undefined") {
          observer = new IntersectionObserver(
            ([entry]) => {
              inView = entry.isIntersecting;
              sync();
            },
            { rootMargin: "80px" }
          );
          observer.observe(host);
        } else {
          inView = true;
        }

        onVisibility = () => sync();
        document.addEventListener("visibilitychange", onVisibility);

        onThemeChange = () => field?.setPalette(readPalette(host));
        themeQuery.addEventListener?.("change", onThemeChange);

        onMotionChange = () => {
          if (motionQuery.matches) field?.freeze();
          else field?.thaw();
          sync();
        };
        motionQuery.addEventListener?.("change", onMotionChange);

        sync();
      } catch {
        if (!cancelled) {
          setReady(false);
          report("failed");
        }
      }
    })();

    return () => {
      cancelled = true;
      observer?.disconnect();
      resizeObserver?.disconnect();
      if (onVisibility) document.removeEventListener("visibilitychange", onVisibility);
      if (onThemeChange) themeQuery.removeEventListener?.("change", onThemeChange);
      if (onMotionChange) motionQuery.removeEventListener?.("change", onMotionChange);
      if (onContextLost) canvas.removeEventListener("webglcontextlost", onContextLost);
      field?.dispose();
      field = null;
    };
  }, [rises]);

  return (
    <div className="mf-host" ref={hostRef} data-ready={ready || undefined}>
      <canvas className="mf-canvas" ref={canvasRef} aria-hidden="true" />
    </div>
  );
}
